import React from 'react';
import { VirtualizedFormSelect } from '@roger/ui-components';
import {
  cliniciansToSelectOptions,
  type ClinicianRole,
  type ClinicianSelectInput,
  type ClinicianSelectOption,
} from '../../utils/clinicianUtils';

type VirtualizedFormSelectProps = React.ComponentProps<
  typeof VirtualizedFormSelect
>;

export interface ClinicianSelectProps extends Omit<
  VirtualizedFormSelectProps,
  'options' | 'formatOptionLabel'
> {
  /** Raw available-clinician (UserClinic) records to choose from. */
  clinicians: ClinicianSelectInput[];
  /** Restrict options to a single assignable role. Omit to include everyone. */
  role?: ClinicianRole;
  /** Override an option's searchable label while keeping shared option building. */
  optionLabelFormatter?: (
    clinician: ClinicianSelectInput,
    defaultLabel: string
  ) => string;
  /**
   * Render one option row (`context: 'menu'`) or the closed control
   * (`context: 'value'`).
   *
   * Typed over {@link ClinicianSelectOption} rather than the design system's
   * `{ value, label }`, so a renderer can reach the person behind the option
   * without asserting a shape.
   */
  formatOptionLabel?: (
    option: ClinicianSelectOption,
    meta: { context: 'menu' | 'value' }
  ) => React.ReactNode;
}

/**
 * Reusable clinician / case-manager / approver picker.
 *
 * Wraps {@link VirtualizedFormSelect} with the shared option builder so labels
 * are formatted consistently (linked clinician → user → email → username) and
 * records with no usable name are dropped instead of rendering as a blank row
 * (ROG-2698). Prefer this over hand-rolling `clinicians.map(...)` at call sites.
 *
 * Integrates with react-hook-form via the usual `{...register('field')}` spread,
 * or use it controlled with `defaultValue` + `onChange`.
 *
 * @example
 * <ClinicianSelect
 *   label="Case Manager"
 *   placeholder="Select case manager..."
 *   clinicians={availableClinicians}
 *   role="caseManager"
 *   required
 *   error={errors.case_manager_id?.message}
 *   {...register('case_manager_id', { onBlur: handleFieldBlur })}
 * />
 */
export const ClinicianSelect = React.forwardRef<
  HTMLInputElement,
  ClinicianSelectProps
>(
  (
    {
      clinicians,
      role,
      optionLabelFormatter,
      formatOptionLabel,
      sortOptions = true,
      ...rest
    },
    ref
  ) => {
    // Memoize so the options array keeps a stable identity across renders (the
    // underlying select re-sorts when it changes).
    const options = React.useMemo(() => {
      const baseOptions = cliniciansToSelectOptions(clinicians, role);
      if (!optionLabelFormatter) return baseOptions;

      const cliniciansById = new Map(clinicians.map(c => [c.id, c]));
      return baseOptions.map(option => {
        const clinician = cliniciansById.get(option.value);
        if (!clinician) return option;
        const label = optionLabelFormatter(clinician, option.label).trim();
        // Spread, never rebuild: the option carries fields the label formatter
        // knows nothing about, and a renderer downstream depends on them.
        return label ? { ...option, label } : option;
      });
    }, [clinicians, optionLabelFormatter, role]);
    return (
      <VirtualizedFormSelect
        ref={ref}
        options={options}
        sortOptions={sortOptions}
        formatOptionLabel={
          formatOptionLabel
            ? (option, meta) =>
                // Every option in this list was built by
                // `cliniciansToSelectOptions` above, and react-select hands the
                // option object itself to the renderer. The design system's
                // prop type names only the two fields it reads, so the wider
                // shape has to be restated here rather than at each call site.
                formatOptionLabel(option as ClinicianSelectOption, meta)
            : undefined
        }
        {...rest}
      />
    );
  }
);

ClinicianSelect.displayName = 'ClinicianSelect';
